
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, CreditCard, Loader2, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";
import { useSubscription } from "@/hooks/use-subscription";
import { useAvatar } from "@/hooks/use-avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const MobileAuthSection = ({ onClose }: { onClose: () => void }) => {
  const { user } = useAuth();
  const { subscribed: isPremium } = useSubscription();
  const { avatarUrl } = useAvatar();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const userName = user?.user_metadata?.first_name || null;
  const userLastName = user?.user_metadata?.last_name || null;
  const initials = userName
    ? `${userName.charAt(0)}${userLastName ? userLastName.charAt(0) : ""}`.toUpperCase()
    : user?.email?.charAt(0).toUpperCase() || "?";

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        onClose();
        window.open(data.url, "_blank");
      }
    } catch (err: any) {
      toast.error("Could not open billing portal", { description: err.message });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await supabase.auth.signOut();
      onClose();
    } catch (err: any) {
      toast.error("Could not sign out", { description: err.message });
    } finally {
      setIsSigningOut(false);
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col space-y-3">
        <Link 
          to="/auth/login" 
          className="flex items-center justify-center px-4 py-2 font-medium border border-grandpal-green rounded-md text-grandpal-green hover:bg-grandpal-green/10 transition-colors"
          onClick={onClose}
        >
          Sign In
        </Link>
        <Link 
          to="/auth/signup" 
          className="flex items-center justify-center px-4 py-2 font-medium rounded-md bg-grandpal-green text-white hover:bg-grandpal-green-dark transition-colors"
          onClick={onClose}
        >
          Sign up for Free
        </Link>
      </div>
    );
  }
  
  return (
    <div className="bg-grandpal-beige/50 p-4 rounded-lg">
      <div className="flex items-center gap-3">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Profile"
            className="w-10 h-10 rounded-full object-cover shrink-0 border border-border"
          />
        ) : (
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
            {initials}
          </span>
        )}
        <div>
          <h3 className="text-grandpal-green font-medium text-lg leading-tight">
            {`Hi ${userName || 'there'}`}
          </h3>
          {isPremium && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              <Crown className="h-3 w-3" />
              Premium
            </span>
          )}
        </div>
      </div>
      <div className="mt-3 space-y-2">
        <Link 
          to="/dashboard" 
          className="flex items-center text-gray-700 hover:text-grandpal-green transition-colors group"
          onClick={onClose}
        >
          <span className="flex-1">My Dashboard</span>
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Link>

        <Link 
          to="/settings" 
          className="flex items-center text-gray-700 hover:text-grandpal-green transition-colors group"
          onClick={onClose}
        >
          <Settings className="h-4 w-4 mr-2" />
          <span className="flex-1">Settings</span>
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Link>

        {isPremium ? (
          <button
            onClick={handleManageSubscription}
            disabled={isLoadingPortal}
            className="w-full flex items-center text-primary hover:text-primary/80 transition-colors group disabled:opacity-60"
          >
            {isLoadingPortal ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4 mr-2" />
            )}
            <span className="flex-1 text-left">Manage subscription</span>
            {!isLoadingPortal && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        ) : (
          <Link 
            to="/premium" 
            className="flex items-center font-medium text-amber-600 hover:text-amber-700 transition-colors group"
            onClick={onClose}
          >
            <Crown className="h-4 w-4 mr-2 text-amber-500" />
            <span className="flex-1">Go Premium</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}

        <button
          onClick={handleSignOut}
          disabled={isSigningOut}
          className="w-full flex items-center text-destructive hover:text-destructive/80 transition-colors group disabled:opacity-60 pt-2 border-t border-border mt-1"
        >
          {isSigningOut ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <LogOut className="h-4 w-4 mr-2" />
          )}
          <span className="flex-1 text-left">Sign out</span>
        </button>
      </div>
    </div>
  );
};

export default MobileAuthSection;
